'use client'

import { createContext, useContext, useState, useMemo, ReactNode } from 'react'

interface BuilderActions {
  onSave: () => void
  onDiscard: () => void
  isSaving?: boolean
  isDeleting?: boolean
  hasUnsavedChanges?: boolean
}

interface BuilderContextType {
  actions: BuilderActions | null
  setActions: (actions: BuilderActions | null) => void
}

const BuilderContext = createContext<BuilderContextType | null>(null)

export function BuilderProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<BuilderActions | null>(null)

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({ actions, setActions }), [actions])

  return (
    <BuilderContext.Provider value={value}>{children}</BuilderContext.Provider>
  )
}

export function useBuilderContext() {
  const context = useContext(BuilderContext)
  if (context === null) {
    throw new Error('useBuilderContext must be used within a BuilderProvider')
  }
  return context
}

export function useOptionalBuilderContext() {
  return useContext(BuilderContext)
}
